import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';

const EmailVerification = () => {
  const [status, setStatus] = useState('pending');
  const [message, setMessage] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = location.state?.email;
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
  }, [token]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [countdown]);

  const verifyEmail = async (token) => {
    try {
      const response = await authService.verifyEmail(token);
      
      if (response.isVerified) {
        setStatus('verified');
        setMessage('Email verified successfully! You can now login.');
        setTimeout(() => {
          navigate('/auth/login');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(response.message);
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.message || 'Verification failed. Please try again.');
    }
  };

  const handleResendEmail = async () => {
    try {
      setResendDisabled(true);
      setCountdown(60);
      
      const response = await authService.sendEmailVerification(email);
      
      if (response.success) {
        setMessage('Verification email has been resent. Please check your inbox.');
      } else {
        if (response.message === "Email is already verified") {
          setMessage('Email is already verified. Redirecting to login');
          setTimeout(() => {
            navigate('/auth/login');
          }, 2000);
        } else {
          setMessage(response.message || 'Failed to send verification email.');
        }
      }
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to resend verification email.');
      setResendDisabled(false);
      setCountdown(0);
    }
  };

  if (!email && !token) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100">
        <Card className="p-4 shadow" style={{ maxWidth: '400px' }}>
          <Card.Body className="text-center">
            <h5>Invalid Access</h5>
            <p>Please sign up first to verify your email.</p>
            <Button variant="primary" onClick={() => navigate('/auth/signup')}>
              Go to Sign Up
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="d-flex justify-content-center align-items-center vh-100">
      <Card className="p-4 shadow" style={{ maxWidth: '400px' }}>
        <Card.Body className="text-center">
          <h5>Email Verification</h5>
          
          {status === 'pending' && (
            <>
              <p>We've sent a verification email to <strong>{email}</strong>.</p>
              <p>Please check your inbox and click the verification link.</p>
              <Button 
                variant="primary" 
                onClick={handleResendEmail} 
                disabled={resendDisabled}
              >
                {resendDisabled 
                  ? `Resend Email (${countdown}s)` 
                  : 'Resend Verification Email'}
              </Button>
            </>
          )}

          {message && (
            <Alert variant={status === 'verified' ? 'success' : 'danger'} className="mt-3">
              {message}
            </Alert>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EmailVerification; 